import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, CreditCard, FileText, Info, RefreshCw, X } from 'lucide-react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';

import CustomerNavbar from '../components/CustomerNavbar';
import Footer from '../components/Footer';
import InvoicePaymentAmountPanel from '../components/InvoicePaymentTipSection';
import apiService from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { formatDateOnly } from '../utils/dateOnly';
import { getInvoiceAmountDue } from '../utils/invoiceAmountDue';

type InvoiceStatus = 'DRAFT' | 'SENT' | 'PAID' | 'VOIDED' | string;

type InvoiceRow = {
  id: string;
  business_id: string;
  appointment_id?: string | null;
  bill_to: string;
  customer_email?: string | null;
  invoice_date: string;
  due_date: string;
  status: InvoiceStatus;
  total: string | number;
  deposit?: string | number;
  due?: string | number;
  business?: any;
};

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const safeNum = (v: any): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
};

const formatMoney = (v: any): string => safeNum(v).toFixed(2);

const toInvoiceLabel = (id: string): string => {
  const short = (id || '').split('-')[0]?.toUpperCase() || '—';
  return `INV-${short}`;
};

const fmtDate = (v: any): string => formatDateOnly(v, 'en-US', { year: 'numeric', month: 'short', day: 'numeric' });

const getBusinessName = (row: InvoiceRow | null): string => {
  const b: any = (row as any)?.business;
  const name = String(b?.name ?? b?.business_name ?? '').trim();
  return name || 'Business';
};

const getInvoiceDueAmount = (invoice: InvoiceRow): number => getInvoiceAmountDue(invoice);

function CheckoutForm(props: {
  invoiceId: string;
  guestToken: string;
  tipUpdating: boolean;
  totalAmount: number;
  onCancel: () => void;
}) {
  const { invoiceId, guestToken, tipUpdating, totalAmount, onCancel } = props;
  const navigate = useNavigate();

  const stripe = useStripe();
  const elements = useElements();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);

      if (!stripe || !elements) return;
      if (tipUpdating) return;

      try {
        setSubmitting(true);

        const { error: syncError } = await elements.fetchUpdates();
        if (syncError) {
          setError(syncError.message || 'Could not refresh your payment total. Please try again.');
          return;
        }

        const returnUrl = `${window.location.origin}/guest/invoices/${encodeURIComponent(invoiceId)}/pay/complete?t=${encodeURIComponent(guestToken)}`;
        const result = await stripe.confirmPayment({
          elements,
          confirmParams: { return_url: returnUrl },
          redirect: 'if_required',
        });

        if (result.error) {
          setError(result.error.message || 'Payment failed. Please try again.');
          return;
        }

        const pi = result.paymentIntent;
        if (pi?.id) {
          try {
            await apiService.syncPublicInvoicePaymentIntent(invoiceId, pi.id, guestToken);
          } catch {
            // Non-fatal; completion page will retry.
          }
          navigate(
            `/guest/invoices/${encodeURIComponent(invoiceId)}/pay/complete?t=${encodeURIComponent(guestToken)}&payment_intent=${encodeURIComponent(pi.id)}`
          );
        } else {
          navigate(`/guest/invoices/${encodeURIComponent(invoiceId)}?t=${encodeURIComponent(guestToken)}`);
        }
      } catch (err: any) {
        setError(err?.message || 'Payment failed. Please try again.');
      } finally {
        setSubmitting(false);
      }
    },
    [elements, guestToken, invoiceId, navigate, stripe, tipUpdating]
  );

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="text-xs text-gray-500">Payments are processed securely by Stripe. Do not refresh the page while processing.</div>

        <div className="bg-white border border-gray-200 rounded-2xl p-4">
          <PaymentElement />
        </div>

        {error ? <div className="text-sm text-red-600">{error}</div> : null}

        <label className="flex items-center gap-3 cursor-pointer md:justify-center">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="h-4 w-4 shrink-0 rounded border-gray-300 text-[#0B4778] focus:ring-[#0B4778]"
            aria-describedby="guest-agree-desc"
          />
          <span id="guest-agree-desc" className="text-sm text-gray-700">
            I agree to pay ${formatMoney(totalAmount)} and authorize this charge.
          </span>
        </label>

        <div className="grid grid-cols-2 gap-3 md:flex md:justify-center">
          <button
            type="button"
            onClick={onCancel}
            className="aacore-btn-outlined w-full md:w-auto justify-center inline-flex items-center gap-2"
            disabled={submitting}
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
          <button type="submit" className="aacore-btn-primary w-full md:w-auto justify-center" disabled={!stripe || !elements || submitting || tipUpdating || !agreed}>
            <CreditCard className="w-4 h-4" />
            {submitting ? 'Processing…' : 'Pay now'}
          </button>
        </div>
      </form>

      {submitting ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" aria-modal="true" role="dialog" aria-live="polite" aria-busy="true">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 max-w-sm w-full text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B4778] mx-auto mb-4" aria-hidden />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Processing your payment</h2>
            <p className="text-sm text-gray-600 mb-0">Please wait while your payment is being processed. Do not refresh or close this page.</p>
          </div>
        </div>
      ) : null}
    </>
  );
}

const GuestInvoicePaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const query = useQuery();
  const guestToken = query.get('t') || '';

  const { user, logout } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invoice, setInvoice] = useState<InvoiceRow | null>(null);

  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [tipAmount, setTipAmount] = useState(0);
  const [tipUpdating, setTipUpdating] = useState(false);

  const businessName = useMemo(() => getBusinessName(invoice), [invoice]);
  const invoiceDueAmount = useMemo(() => (invoice ? getInvoiceDueAmount(invoice) : 0), [invoice]);
  const totalDueAmount = invoiceDueAmount + tipAmount;

  const handleTipChange = useCallback(
    async (tipDollars: number) => {
      if (!invoiceId || !paymentIntentId || !guestToken) {
        throw new Error('Payment is not ready yet.');
      }
      const normalizedTip = Math.max(0, Math.round(tipDollars * 100) / 100);
      const tipCents = Math.round(normalizedTip * 100);
      setTipUpdating(true);
      try {
        const result = await apiService.updatePublicInvoicePaymentIntentTip(invoiceId, paymentIntentId, guestToken, tipCents);
        setTipAmount(normalizedTip);
        setClientSecret(result.clientSecret);
        setPaymentIntentId(result.paymentIntentId);
      } finally {
        setTipUpdating(false);
      }
    },
    [guestToken, invoiceId, paymentIntentId]
  );

  useEffect(() => {
    (async () => {
      try {
        const cfg = await apiService.getStripeConfig();
        setStripePromise(loadStripe(cfg.publishableKey));
      } catch (e: any) {
        console.error('Failed to load Stripe config', e);
        setError(e?.response?.data?.error || e?.message || 'Stripe is not configured');
      }
    })();
  }, []);

  const load = useCallback(async () => {
    if (!invoiceId) return;
    if (!guestToken) {
      setLoading(false);
      setInvoice(null);
      setClientSecret(null);
      setPaymentIntentId(null);
      setError('This payment link is missing a guest token. Please use the link from your email.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const inv = await apiService.getPublicInvoice(invoiceId, guestToken);
      setInvoice(inv as any);

      const pi = await apiService.createPublicInvoicePaymentIntent(invoiceId, guestToken);
      setClientSecret(pi.clientSecret);
      setPaymentIntentId(pi.paymentIntentId);
    } catch (e: any) {
      console.error('Failed to initialize guest invoice payment', e);
      setInvoice(null);
      setClientSecret(null);
      setPaymentIntentId(null);
      setError(e?.response?.data?.error || 'Failed to start payment');
    } finally {
      setLoading(false);
    }
  }, [invoiceId, guestToken]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!invoice) return;
    const s = String(invoice.status || '').toUpperCase();
    if (s === 'PAID') toast('This invoice is already paid.');
  }, [invoice]);

  if (!invoiceId) {
    return (
      <div className="min-h-screen no-animations flex flex-col relative">
        <CustomerNavbar onLogout={logout} showDashboardLink={!!user} showLogoutButton={!!user} showAuthButtons={!user} />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <FileText className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Invoice Not Found</h1>
            <p className="text-gray-600 mb-6">Missing invoice ID.</p>
            <button onClick={() => navigate('/')} className="aacore-btn-primary">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen no-animations flex flex-col relative">
        <CustomerNavbar onLogout={logout} showDashboardLink={!!user} showLogoutButton={!!user} showAuthButtons={!user} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto mb-4"></div>
            <p className="text-gray-600">Starting secure payment…</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !invoice || !stripePromise || !clientSecret) {
    return (
      <div className="min-h-screen no-animations flex flex-col relative">
        <CustomerNavbar onLogout={logout} showDashboardLink={!!user} showLogoutButton={!!user} showAuthButtons={!user} />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <FileText className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Unable to Start Payment</h1>
            <p className="text-gray-600 mb-6">{error || 'Payment is unavailable'}</p>
            <div className="flex gap-2 justify-center">
              <button onClick={() => load()} className="aacore-btn-primary inline-flex items-center gap-2">
                <RefreshCw className="w-4 h-4" />
                Try again
              </button>
              <button
                onClick={() => navigate(`/guest/invoices/${encodeURIComponent(invoiceId)}?t=${encodeURIComponent(guestToken)}`)}
                className="aacore-btn-outlined inline-flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to invoice
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const isPayable = String(invoice.status || '').toUpperCase() === 'SENT';

  return (
    <div className="min-h-screen no-animations flex flex-col relative">
      <CustomerNavbar onLogout={logout} showDashboardLink={!!user} showLogoutButton={!!user} showAuthButtons={!user} />

      <div className="flex-1 pt-6 pb-12 overflow-visible">
        <div className="px-4 lg:px-8 max-w-3xl mx-auto overflow-visible">
          <div className="mb-4">
            <Link
              to={`/guest/invoices/${encodeURIComponent(invoiceId)}?t=${encodeURIComponent(guestToken)}`}
              className="flex items-center font-semibold"
              style={{ color: '#0B4778' }}
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Invoice
            </Link>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs text-gray-500 mb-1">PAY INVOICE</div>
                <h1 className="text-xl font-bold mb-0">{toInvoiceLabel(invoice.id)}</h1>
                <p className="text-gray-600 text-sm mb-0">
                  {businessName} • Sent {fmtDate(invoice.invoice_date)} • Due {fmtDate(invoice.due_date)}
                </p>
              </div>
              <InvoicePaymentAmountPanel
                invoiceAmount={invoiceDueAmount}
                tipAmount={tipAmount}
                onTipChange={handleTipChange}
                tipUpdating={tipUpdating}
              />
            </div>

            <div className="mt-3 p-3 border border-gray-200 rounded-xl bg-gray-50 text-sm text-gray-600 flex gap-2">
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
              <div>
                You’re paying <span className="font-semibold text-gray-900">{businessName}</span>. If you have questions about the invoice, contact the
                business before paying.
              </div>
            </div>
          </div>

          {!isPayable ? (
            <div className="bg-white border border-gray-200 rounded-2xl p-4">
              <div className="text-gray-900 font-semibold mb-1">This invoice can’t be paid online</div>
              <div className="text-gray-600 text-sm">Its status is {String(invoice.status || '—')}.</div>
              <div className="mt-3">
                <button
                  onClick={() => navigate(`/guest/invoices/${encodeURIComponent(invoiceId)}?t=${encodeURIComponent(guestToken)}`)}
                  className="aacore-btn-outlined inline-flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to invoice
                </button>
              </div>
            </div>
          ) : (
            <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
              <CheckoutForm
                invoiceId={invoiceId}
                guestToken={guestToken}
                tipUpdating={tipUpdating}
                totalAmount={totalDueAmount}
                onCancel={() => navigate(`/guest/invoices/${encodeURIComponent(invoiceId)}?t=${encodeURIComponent(guestToken)}`)}
              />
            </Elements>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default GuestInvoicePaymentPage;

